import React, { useEffect, useState } from "react";
import { Lock, ShieldCheck, Loader2 } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import Logo from "./Logo";
import { getToken } from "../software/api";
import { NDA_VERSION, NDA_TITLE, NDA_INTRO, NDA_CLAUSES } from "../data/nda";

const STORAGE_KEY = `anx_access_v${NDA_VERSION}`;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NdaGate({ children }) {
  const location = useLocation();
  const [unlocked, setUnlocked] = useState(() => !!localStorage.getItem(STORAGE_KEY));
  const [authDenied, setAuthDenied] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", code: "", agreed: false });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Signed-in users (owner/staff) never need to sign the investor NDA — unlock if a valid session exists.
  // Re-checks on navigation because this gate wraps the router and does not remount per route.
  useEffect(() => {
    if (unlocked || !getToken()) return;
    let active = true;
    fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((me) => {
        if (!active) return;
        if (me && me.id) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ name: me.name, at: new Date().toISOString(), v: NDA_VERSION, via: "login" }));
          setUnlocked(true);
        } else {
          setAuthDenied(true);
        }
      })
      .catch(() => { if (active) setAuthDenied(true); });
    return () => { active = false; };
  }, [unlocked, location.pathname]);

  // Admin login and the admin-only IP brief are exempt from the NDA gate — both have their own auth.
  if (unlocked || location.pathname.startsWith("/software") || location.pathname.startsWith("/tech") || location.pathname.startsWith("/pool-tech") || location.pathname.startsWith("/accept") || location.pathname.startsWith("/set-password") || location.pathname === "/shop/ip") return children;

  // A session token is present and not yet rejected — we're verifying; show a spinner, not the NDA gate.
  if (getToken() && !authDenied) {
    return (
      <div className="min-h-screen bg-[#0A0F1D] flex items-center justify-center" data-testid="nda-verifying">
        <Loader2 className="h-6 w-6 animate-spin text-sky-400" />
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.agreed) { setErr("Please tick the box to accept the NDA."); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/access/unlock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Access denied.");
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ name: data.name, at: new Date().toISOString(), v: data.nda_version, audience: data.audience || "investor" }));
      setUnlocked(true);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: k === "agreed" ? e.target.checked : e.target.value });

  return (
    <div className="min-h-screen bg-[#0A0F1D] text-slate-100 flex items-center justify-center px-4 py-10" data-testid="nda-gate">
      <div className="w-full max-w-2xl">
        <div className="flex items-center justify-between mb-5">
          <Logo dark />
          <div className="flex items-center gap-4">
            <Link to="/software" data-testid="nda-admin-login-link" className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-300 hover:text-sky-200 transition-colors"><Lock className="h-3.5 w-3.5" /> Admin login</Link>
            <span className="hidden sm:inline-flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest text-sky-400">Private preview</span>
          </div>
        </div>

        <div className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur overflow-hidden">
          <div className="p-6 md:p-8 border-b border-white/10">
            <div className="flex items-center gap-2 text-sky-300 text-xs font-mono2 uppercase tracking-widest mb-2"><ShieldCheck className="h-4 w-4" /> Confidential · NDA v{NDA_VERSION}</div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-white">{NDA_TITLE}</h1>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">{NDA_INTRO}</p>
          </div>

          <div className="max-h-[38vh] overflow-y-auto p-6 md:p-8 space-y-4" data-testid="nda-text">
            {NDA_CLAUSES.map((c) => (
              <div key={c.h}>
                <p className="font-display font-bold text-slate-100 text-sm">{c.h}</p>
                <p className="mt-1 text-sm text-slate-400 leading-relaxed">{c.p}</p>
              </div>
            ))}
          </div>

          <form onSubmit={submit} className="p-6 md:p-8 border-t border-white/10 space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400">Full name</label>
                <input value={form.name} onChange={set("name")} required data-testid="nda-name" className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-400" placeholder="Your full name" />
              </div>
              <div>
                <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400">Email</label>
                <input type="email" value={form.email} onChange={set("email")} required data-testid="nda-email" className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-400" placeholder="you@example.com" />
              </div>
            </div>
            <div>
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400">Access password</label>
              <input type="password" value={form.code} onChange={set("code")} required data-testid="nda-code" className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-400" placeholder="Provided by Aqualux Nexus" />
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.agreed} onChange={set("agreed")} data-testid="nda-agree" className="mt-1 h-4 w-4 rounded border-white/20 bg-white/5 accent-sky-500" />
              <span className="text-sm text-slate-300">I have read, understood and agree to be legally bound by the Non-Disclosure Agreement above.</span>
            </label>

            {err && <p className="text-sm text-rose-400" data-testid="nda-error">{err}</p>}

            <button type="submit" disabled={busy} data-testid="nda-submit" className="w-full inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-white font-semibold px-6 py-3.5 rounded-full transition-colors">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifying…</> : <>Agree &amp; Enter</>}
            </button>
            <p className="text-center text-[11px] text-slate-500">Your name, email, IP address and the time of acceptance are recorded with this agreement.</p>
          </form>
        </div>
      </div>
    </div>
  );
}
